import {
  getPriceForCurrency,
  formatPriceWithSymbol
} from '@lib/page-utils/Join';
import { TIER_TO_MEMBERSHIP } from '@lib/constants';
import type { PaywallPlanKey } from '@lib/sanity/types';
import type { SubscriptionPlansResponse } from '@hooks/useSubscriptionPlans';

/**
 * Every annual plan a paywall can offer, cheapest first. `TierComparison` ranks
 * tier cards by index here, so a key that is missing sorts to the end — which
 * is why both arms belong in this list even though no single paywall shows all
 * five. A page renders one arm plus Ultra, and the relative order holds either
 * way.
 *
 * Order is by actual price: Starter £59.76 / £71.76, Pro £95.76 / £119.76,
 * Ultra £359.76.
 */
export const PAYWALL_PLAN_KEYS: PaywallPlanKey[] = [
  'starter_yearly',
  'starter_yearly_alt',
  'pro_yearly',
  'pro_yearly_alt',
  'ultra_yearly'
];

/**
 * The internal plan/membership id a tier checks out — fed to
 * getCheckoutPath / `/checkout-stripe?planId=`. This is NOT a Stripe
 * `price_...` id; the backend (`/billing/checkout`) maps it to the Stripe
 * price server-side.
 */
export const getPlanIdForTier = (plan: PaywallPlanKey): number | undefined =>
  TIER_TO_MEMBERSHIP[plan];

export type ResolvedPlanPricing = {
  /** Per-month figure (annual / 12, promo applied), already symbol-formatted. */
  perMonth: string | null;
  /**
   * Pre-promo per-month figure, already symbol-formatted. Equals perMonth
   * when the effective discount is 0, which suppresses the strike-through.
   */
  fullPerMonth: string | null;
  /** Billed-today annual figure, already symbol-formatted. */
  annual: string | null;
};

/**
 * Resolves a plan's display prices from the live plans API (the same source
 * the /join page uses), so paywall pricing never drifts from Stripe. Returns
 * nulls until the plans have loaded or when the plan is missing.
 *
 * discountPercent comes from the Sanity marketing copy singleton so the
 * displayed discount tracks the Stripe default checkout coupon.
 */
export const resolvePlanPricing = (
  plans: SubscriptionPlansResponse,
  plan: PaywallPlanKey,
  currencyCode = 'gbp',
  discountPercent?: number
): ResolvedPlanPricing => {
  const price = getPriceForCurrency(plans, plan, currencyCode, {
    discountPercent
  });
  if (!price) return { perMonth: null, fullPerMonth: null, annual: null };

  const annualAmount = price.amount * 12;
  return {
    perMonth: price.formattedPriceWithSymbol ?? null,
    fullPerMonth: price.comparisonFormattedPriceWithSymbol ?? null,
    annual: formatPriceWithSymbol(annualAmount, price.currency)
  };
};
